'use client';

import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Checkbox from '@/components/molecules/checkbox/checkbox';
import useMutationUpdatePreBookingCustomer from '@/integrations/booking/pre-booking/customers/update-customer/update-customer.hooks';
import useQuerySimpleCustomer from '@/integrations/content/content-modules/simple-customer/simple-customer.hooks';
import useQueryTermsAndConditions from '@/integrations/content/content-modules/terms-and-conditions/terms-and-conditions.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useBookingFlowContext } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './terms-and-conditions.module.scss';

export interface TermsAndConditionsProps {
  /** The UUID of the module */
  id: Content.Components['TermsAndConditionsHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['TermsAndConditionsHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['TermsAndConditionsHeadlessModuleDto']['parameters'];
}

const TermsAndConditions = ({
  id,
  pageId,
  parameters,
}: TermsAndConditionsProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const {
    activeAnchor,
    anchorSignal,
    moduleStatusMap,
    setActiveAnchor,
    updateModuleStatus,
  } = useBookingFlowContext();
  const identifiers = { ...parameters, id, pageId };
  const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState<
    string[]
  >([]);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isInitializedRef = useRef(false);

  const {
    data: dataTermsAndConditions,
    isLoading: isLoadingTermsAndConditions,
    refetch: refetchTermsAndConditions,
  } = useQueryTermsAndConditions({
    parameters: identifiers,
  });

  const {
    data: dataSimpleCustomer,
    isLoading: isLoadingSimpleCustomer,
    refetch: refetchSimpleCustomer,
  } = useQuerySimpleCustomer({
    parameters: identifiers,
  });

  const hasNoData =
    !isLoadingTermsAndConditions &&
    !isLoadingSimpleCustomer &&
    (!dataTermsAndConditions || !dataSimpleCustomer);

  const { mutate: mutateUpdatePreBookingCustomer } =
    useMutationUpdatePreBookingCustomer();

  useEffect(() => {
    if (!dataTermsAndConditions) return;

    setActiveAnchor(null);
    updateModuleStatus(id, {
      isComplete: dataTermsAndConditions.isComplete,
      isRequired: dataTermsAndConditions.isRequired,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTermsAndConditions]);

  useEffect(() => {
    if (isInitializedRef.current) return;

    if (!dataTermsAndConditions?.terms?.length) return;

    if (!dataSimpleCustomer) return;

    // If customer agreed to terms, restore mandatory terms
    if (dataSimpleCustomer.customer.agreedToTerms) {
      const acceptedRequiredTerms = dataTermsAndConditions.terms
        .filter(term => term.isMandatory)
        .map(term => term.id);

      setAcceptedTermsAndConditions(acceptedRequiredTerms);
    }

    isInitializedRef.current = true;
  }, [
    dataSimpleCustomer?.customer.agreedToTerms,
    dataTermsAndConditions?.terms,
  ]);

  const shouldHighlightErrors = useMemo(() => {
    return (
      activeAnchor === id &&
      !moduleStatusMap[id]?.isComplete &&
      moduleStatusMap[id]?.isRequired
    );
  }, [activeAnchor, id, moduleStatusMap]);

  useEffect(() => {
    refetchTermsAndConditions();
    refetchSimpleCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!componentRef.current) return;

    if (!activeAnchor) return;

    if (activeAnchor === id) {
      componentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeAnchor, anchorSignal, id]);

  const handleAcceptTermsAndConditions = (termId: string) => {
    const updatedAccepted = acceptedTermsAndConditions.includes(termId)
      ? acceptedTermsAndConditions.filter(id => id !== termId)
      : [...acceptedTermsAndConditions, termId];

    setAcceptedTermsAndConditions(updatedAccepted);

    const mandatoryIds = (dataTermsAndConditions?.terms ?? [])
      .filter(term => term.isMandatory)
      .map(term => term.id);

    const agreedToTerms = mandatoryIds.every(id =>
      updatedAccepted.includes(id),
    );
    const wasAgreedToTerms = mandatoryIds.every(id =>
      acceptedTermsAndConditions.includes(id),
    );

    // Only call the API when the overall agreed state changes
    if (agreedToTerms === wasAgreedToTerms) return;

    mutateUpdatePreBookingCustomer({
      brand,
      customer: { updatedFields: { agreedToTerms } },
      invalidationKeys: [QueryId.SIMPLE_CUSTOMER, QueryId.TERMS_AND_CONDITIONS],
      parameters: identifiers,
    });
  };

  const getLoadingState = () => {
    return (
      <div className={styles.loaderWrapper}>
        <SkeletonLoader
          className={classNames(styles.loaderHeadline, styles.loader)}
          height="32px"
        />

        <SkeletonLoader
          className={classNames(styles.loaderText, styles.loader)}
          height="24px"
        />

        <SkeletonLoader
          className={classNames(styles.loaderText, styles.loader)}
          height="24px"
        />

        <SkeletonLoader
          className={classNames(styles.loaderText, styles.loader)}
          height="24px"
        />

        <SkeletonLoader
          className={classNames(styles.loaderCheckbox, styles.loader)}
          height="20px"
        />
      </div>
    );
  };

  if (hasNoData) return null;

  return (
    <div ref={componentRef} className={styles.component}>
      {(isLoadingTermsAndConditions || isLoadingSimpleCustomer) &&
        getLoadingState()}

      {dataTermsAndConditions &&
        dataSimpleCustomer &&
        dataTermsAndConditions.terms.map(term => {
          const shouldShowError =
            shouldHighlightErrors &&
            term.isMandatory &&
            !acceptedTermsAndConditions.includes(term.id);

          return (
            <div key={term.id} className={styles.term}>
              <div className={styles.headlineWrapper}>
                <Typography className={styles.headline} tagStyle="headingSmall">
                  {term.headline}
                </Typography>

                {shouldShowError && (
                  <Typography
                    className={styles.error}
                    text={`*${t('items.modules.bookingFlow.required')}`}
                  />
                )}
              </div>

              <RichText text={term.description} />

              {!term.hideCheckbox && (
                <Checkbox
                  centerAlignCheckbox={false}
                  checked={acceptedTermsAndConditions?.includes(term.id)}
                  className={styles.checkbox}
                  error={
                    shouldShowError
                      ? t('items.modules.bookingFlow.termsRequiredValidation')
                      : undefined
                  }
                  label={term.acceptText}
                  onChange={() => handleAcceptTermsAndConditions(term.id)}
                  required={term.isMandatory}
                />
              )}
            </div>
          );
        })}
    </div>
  );
};

TermsAndConditions.displayName = 'TermsAndConditions';

export default TermsAndConditions;
